/**
 * AnomalyResolver - Enhanced Anomaly Resolution Interface
 * =========================================================
 * 
 * Advanced anomaly resolution component with:
 * - Revenue timeline with anomaly markers and hover details
 * - Diagnostic AI suggestions (not generic restatements)
 * - Category breakdown showing deltas: "Catering: $28K → $42K (+$14.4K)"
 * - Top transactions driving each anomaly
 * - Batch resolution with filters (spikes, drops, pre-loss, loss-period)
 * - Resolutions flow directly into expert report narrative
 * 
 * Works for ALL business types - categories come from actual data.
 */

import React, { useState, useMemo } from 'react';
import { humanizeLabel } from '@/utils/formatters';
import { anomalySeverityStyle } from '@/utils/anomalySeverity';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  X,
  Check,
  Sparkles,
  Loader2,
  TrendingUp,
  TrendingDown,
  FileText,
  Zap,
  Filter,
  RefreshCw,
  ArrowRight,
  List,
  HelpCircle
} from 'lucide-react';

// Resolution reasons that work for any business type
const RESOLUTION_REASONS = [
  "Seasonal variation",
  "Loss event impact", 
  "Insurance proceeds",
  "One-time event",
  "Misclassification",
  "Recovery period",
  "COVID-19 impact",
  "Normal fluctuation",
  "Verified with client",
  "Requires further review"
];

// Batch filter options. (No per-filter `color` field: it was dead — the render
// maps every active filter to the single brand signal via ACTIVE_FILTER_STYLE,
// never to a per-filter hue — and one value was `purple`, a banned hue. Removed
// so a banned color can't leak back in via a future consumer.)
const BATCH_FILTERS = [
  { id: 'all', label: 'All Unresolved' },
  { id: 'spikes', label: 'Spikes Only' },
  { id: 'drops', label: 'Drops Only' },
  { id: 'high', label: 'High Priority' },
  { id: 'pre-loss', label: 'Pre-Loss Period' },
  { id: 'loss-period', label: 'Loss Period' },
];

// Active-filter styling — every severity/period filter maps to the single brand
// signal color (this is an anomaly UI; only "resolved" is green elsewhere).
const ACTIVE_FILTER_STYLE = {
  backgroundColor: 'var(--color-needs-human-tint)',
  borderColor: 'var(--color-needs-human-tint-bd)',
  color: 'var(--color-needs-human-text)',
};

interface CategoryBreakdown {
  name: string;
  prev: number;
  curr: number;
  delta: number;
}

interface FlaggedTransaction {
  id?: string;
  date: string;
  description: string;
  amount: number;
  flag: boolean;
  category?: string;
}

interface EnhancedAnomaly {
  id: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  type: string;
  title: string;
  description: string;
  percent: number;
  from: number;
  to: number;
  // Whether this anomaly has meaningful from→to semantics. Revenue spikes/drops
  // do; duplicate/COGS/Benford/utility_gap do NOT (their from/to are 0). Set by
  // the accountant mapping; when undefined (wizard path) the render falls back to
  // a value heuristic. Prevents "$0 → $0 / 0.0%" on non-change cards.
  hasDelta?: boolean;
  relatedAmount?: number | null;
  periodFrom: string;
  periodTo: string;
  period: string;
  deviation: number;
  categories?: CategoryBreakdown[];
  transactions?: FlaggedTransaction[];
  aiSuggestion?: string;
  aiConfidence?: number;
  sourceDoc?: string;
  lossEvent?: boolean;
  is_resolved?: boolean;
  resolution_note?: string;
  resolved_at?: string;
  resolution_reason?: string;
  // Event period flags
  isPreLoss?: boolean;
  isLossPeriod?: boolean;
  isPostLoss?: boolean;
  // Original anomaly fields
  anomaly_type?: string;
  explanation?: string;
  ai_analysis?: {
    explanation?: string;
    likely_false_positive?: boolean;
    recommendation?: string;
    confidence?: number;
  };
  // Forensic-insight cache fields. Both are populated by the
  // /api/v1/forensic/anomaly/insight endpoint and consumed at L574+L588+L592.
  // Restored as narrow optionals per archaeology pattern §1 — the interface
  // had drifted away from the shape the component already reads.
  forensic_analysis?: any;
  forensic_insight?: string;
}

interface PeriodData {
  month: string;
  revenue: number;
  anomaly?: 'spike' | 'drop';
  isLossPeriod?: boolean;
}

interface AnomalyResolverProps {
  anomalies: EnhancedAnomaly[];
  periodsData?: PeriodData[];
  totalTransactions?: number;
  totalMonths?: number;
  lossEventStart?: string;
  lossEventEnd?: string;
  onResolve: (anomalyId: string, reason: string, note: string) => void;
  onBatchResolve: (anomalyIds: string[], reason: string, note: string) => void;
  onUnresolve?: (anomalyId: string) => void;
  onViewTransaction?: (transactionId: string) => void;
  onForensicInsightFetched?: (anomalyId: string, insight: any) => void;
  onExplainAnomaly?: (anomalyId: string) => void;
  loading?: boolean;
}

// Format currency compact
const fmtCompact = (n: number) => {
  const abs = Math.abs(n);
  if (abs >= 1000000) return "$" + (n / 1000000).toFixed(1) + "M";
  if (abs >= 1000) return "$" + (n / 1000).toFixed(1) + "K";
  return "$" + n.toFixed(0);
};

// Format percentage
const pct = (n: number) => (n > 0 ? "+" : "") + n.toFixed(1) + "%";

// Generate diagnostic AI suggestion based on context
function generateDiagnosticSuggestion(anomaly: EnhancedAnomaly, lossEventStart?: string): string {
  const isSpike = anomaly.deviation > 0;
  const absPct = Math.abs(anomaly.deviation);
  const delta = anomaly.to - anomaly.from;
  
  // Find the top category driving the change
  const topCategory = anomaly.categories?.reduce((max, cat) => 
    Math.abs(cat.delta) > Math.abs(max.delta) ? cat : max
  , anomaly.categories[0]);
  
  const topCatText = topCategory 
    ? `${fmtCompact(Math.abs(topCategory.delta))} ${isSpike ? 'increase' : 'drop'} in ${topCategory.name}`
    : '';

  // Check if this coincides with loss event
  if (lossEventStart && anomaly.period >= lossEventStart && !isSpike) {
    return `This period coincides with the claimed loss event start date. ${absPct.toFixed(0)}% decline driven by ${topCatText}. Recommend classifying as loss period onset.`;
  }
  
  if (anomaly.isLossPeriod && !isSpike) {
    return `Loss period impact confirmed. Revenue down ${fmtCompact(Math.abs(delta))} (${absPct.toFixed(0)}%) from baseline. ${topCatText}. Flag as covered loss for claim calculation.`;
  }
  
  if (anomaly.isPreLoss && isSpike) {
    return `Pre-loss spike detected. Revenue ${fmtCompact(delta)} above trend. ${topCatText}. Consider using this period in baseline calculation for stronger claim support.`;
  }
  
  if (anomaly.isPostLoss && isSpike) {
    return `Recovery period indicator. Revenue rebounding ${pct(anomaly.deviation)} toward baseline. ${topCatText}. May signal end of covered loss period.`;
  }
  
  // Generic but still diagnostic
  if (isSpike) {
    return `Unusual revenue spike of ${pct(anomaly.deviation)}. ${topCatText}. Review for one-time events, insurance proceeds, or timing differences that may need adjustment.`;
  } else {
    return `Revenue declined ${pct(Math.abs(anomaly.deviation))} from prior period. ${topCatText}. Verify if operational issue, seasonal pattern, or early loss indicator.`;
  }
}

// Compute "nice" Y-axis ticks for readable grid lines
function computeYTicks(minVal: number, maxVal: number, targetCount = 4): { value: number; label: string }[] {
  const range = maxVal - minVal;
  if (range <= 0) return [{ value: minVal, label: fmtCompact(minVal) }];

  const rawStep = range / targetCount;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const residual = rawStep / magnitude;
  let niceStep: number;
  if (residual <= 1.5) niceStep = 1 * magnitude;
  else if (residual <= 3.5) niceStep = 2.5 * magnitude;
  else if (residual <= 7.5) niceStep = 5 * magnitude;
  else niceStep = 10 * magnitude;

  const niceMin = Math.floor(minVal / niceStep) * niceStep;
  const niceMax = Math.ceil(maxVal / niceStep) * niceStep;

  const ticks: { value: number; label: string }[] = [];
  for (let v = niceMin; v <= niceMax; v += niceStep) {
    ticks.push({ value: v, label: fmtCompact(v) });
  }
  return ticks;
}

// Enhanced chart - full revenue timeline with axes, grid lines, and anomaly markers
function Sparkline({
  data,
  activeMonth,
  lossEventStart,
  lossEventEnd,
  onHoverMonth,
  onClickMonth
}: {
  data: PeriodData[];
  activeMonth?: string;
  lossEventStart?: string;
  lossEventEnd?: string;
  onHoverMonth?: (month: string | null) => void;
  onClickMonth?: (month: string) => void;
}) {
  const [hovered, setHovered] = useState<number | null>(null);

  if (!data || data.length === 0) return null;

  const rawMax = Math.max(...data.map(d => d.revenue));
  const rawMin = Math.min(...data.map(d => d.revenue));

  // Layout constants — wide viewBox (~8:1) so the chart fills the full width
  // of the timeline card instead of fitting-to-height and centering (which
  // left big empty margins). Height/Y-mapping unchanged, so axis labels and
  // markers keep the same on-screen size; only the 48 months spread out
  // horizontally for legibility.
  const W = 800;
  const H = 100;
  const MARGIN = { top: 6, right: 14, bottom: 14, left: 40 };
  const plotW = W - MARGIN.left - MARGIN.right;
  const plotH = H - MARGIN.top - MARGIN.bottom;   // 80

  // Y-axis ticks with "nice" rounding
  const yTicks = computeYTicks(rawMin, rawMax);
  const yMin = yTicks[0].value;
  const yMax = yTicks[yTicks.length - 1].value;
  const yRange = yMax - yMin || 1;

  // X-axis tick interval based on data density
  const xTickInterval = data.length > 36 ? 12 : data.length > 12 ? 6 : data.length > 6 ? 3 : 1;
  const xTicks = data
    .map((d, i) => ({ index: i, label: d.month }))
    .filter((_, i) => i % xTickInterval === 0 || i === data.length - 1);

  // Map data to SVG coordinates within the plot area
  const points = data.map((d, i) => {
    const isInLossPeriod = lossEventStart && lossEventEnd &&
      d.month >= lossEventStart && d.month <= lossEventEnd;
    return {
      x: MARGIN.left + (i / Math.max(data.length - 1, 1)) * plotW,
      y: MARGIN.top + plotH - ((d.revenue - yMin) / yRange) * plotH,
      isInLossPeriod,
      index: i,
      ...d
    };
  });

  // Build the path
  const pathD = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(" ");

  // Area fill closes to plot bottom
  const areaD = pathD + `L${points[points.length - 1].x.toFixed(2)},${MARGIN.top + plotH}L${points[0].x.toFixed(2)},${MARGIN.top + plotH}Z`;

  // Find loss period x range for shading
  const lossPoints = points.filter(p => p.isInLossPeriod);
  const lossStartX = lossPoints.length > 0 ? Math.min(...lossPoints.map(p => p.x)) : null;
  const lossEndX = lossPoints.length > 0 ? Math.max(...lossPoints.map(p => p.x)) : null;

  return (
    <div className="relative w-full h-full">
      {/* Tooltip - positioned outside overflow container */}
      {hovered !== null && points[hovered] && (
        <div
          className="cs-meta absolute -top-14 px-3 py-2 rounded-lg shadow-lg whitespace-nowrap z-50 pointer-events-none"
          style={{
            left: `${Math.min(Math.max((points[hovered].x / W) * 100, 10), 90)}%`,
            transform: 'translateX(-50%)',
            background: 'var(--color-bg-elevated)',
            color: 'var(--color-text-primary)',
            border: '1px solid var(--color-border)',
          }}
        >
          <div style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{points[hovered].month}</div>
          <div className="flex items-center gap-2 mt-0.5">
            <span style={{ color: 'var(--color-text-secondary)' }}>{fmtCompact(points[hovered].revenue)}</span>
            {points[hovered].anomaly && (
              <span
                className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              >
                {points[hovered].anomaly === 'drop' ? 'DROP' : 'SPIKE'}
              </span>
            )}
          </div>
          {/* Arrow */}
          <div
            className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 rotate-45"
            style={{ background: 'var(--color-bg-elevated)' }}
          />
        </div>
      )}

      {/* Chart container */}
      <div className="w-full h-full">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full h-full cursor-crosshair"
          preserveAspectRatio="xMidYMid meet"
          onMouseLeave={() => { setHovered(null); onHoverMonth?.(null); }}
        >

        {/* Y-axis grid lines and labels */}
        {yTicks.map((tick) => {
          const y = MARGIN.top + plotH - ((tick.value - yMin) / yRange) * plotH;
          return (
            <g key={tick.value}>
              <line
                x1={MARGIN.left} y1={y}
                x2={W - MARGIN.right} y2={y}
                stroke="var(--color-border)"
                strokeWidth="0.3"
                strokeDasharray="2,2"
              />
              <text
                x={MARGIN.left - 2}
                y={y + 1.8}
                textAnchor="end"
                fill="var(--color-text-dim)"
                fontSize={5}
                fontFamily="system-ui, sans-serif"
              >
                {tick.label}
              </text>
            </g>
          );
        })}

        {/* X-axis labels */}
        {xTicks.map(({ index, label }, i) => {
          const x = MARGIN.left + (index / Math.max(data.length - 1, 1)) * plotW;
          return (
            <text
              key={label}
              x={x}
              y={H - 2}
              textAnchor={i === 0 ? "start" : i === xTicks.length - 1 ? "end" : "middle"}
              fill="var(--color-text-dim)"
              fontSize={4.5}
              fontFamily="system-ui, sans-serif"
            >
              {label}
            </text>
          );
        })}

        {/* Loss period shading — a neutral white-alpha band (K3 review
            2026-08-23: never a brown/orange gradient; the band is terrain, not a
            data series). */}
        {lossStartX !== null && lossEndX !== null && (
          <rect
            x={lossStartX}
            y={MARGIN.top}
            width={lossEndX - lossStartX + 2}
            height={plotH}
            fill="rgba(255,255,255,0.04)"
          />
        )}

        {/* Flat revenue line only — the gradient area fill was banned (K3 P0-3:
            gradients are not allowed; the line is the single data accent). */}

        {/* Revenue line */}
        <path d={pathD} fill="none" stroke="var(--color-text-muted)" strokeWidth="1.2" />

        {/* Always-visible anomaly markers — spikes vs drops use distinct ramp
            colors so the two anomaly types are tellable apart on the line. */}
        {points.filter(p => p.anomaly).map((p, i) => (
          <circle
            key={`anomaly-${i}`}
            cx={p.x}
            cy={p.y}
            r={2.5}
            fill={p.anomaly === 'drop' ? 'var(--color-blocker)' : 'var(--chart-1)'}
            stroke="var(--color-bg-surface)"
            strokeWidth="0.8"
            style={{ pointerEvents: 'none' }}
          />
        ))}

        {/* Invisible hover zones for each point */}
        {points.map((p, i) => (
          <rect
            key={i}
            x={p.x - (plotW / points.length / 2)}
            y={MARGIN.top}
            width={plotW / points.length}
            height={plotH}
            fill="transparent"
            onMouseEnter={() => { setHovered(i); onHoverMonth?.(p.month); }}
            onClick={() => p.anomaly && onClickMonth?.(p.month)}
            className={p.anomaly ? 'cursor-pointer' : 'cursor-crosshair'}
          />
        ))}

        {/* Hover highlight marker */}
        {hovered !== null && points[hovered] && (
          <circle
            cx={points[hovered].x}
            cy={points[hovered].y}
            r={4}
            fill="var(--color-brand)"
            stroke="var(--color-bg-surface)"
            strokeWidth="1.5"
          />
        )}

        {/* Active month marker (when anomaly card is expanded) */}
        {activeMonth && points.find(p => p.month === activeMonth) && (
          <circle
            cx={points.find(p => p.month === activeMonth)!.x}
            cy={points.find(p => p.month === activeMonth)!.y}
            r={5}
            fill="var(--color-brand)"
            stroke="var(--color-bg-surface)"
            strokeWidth="2"
          />
        )}
      </svg>
      </div>
    </div>
  );
}

// Enhanced Category Breakdown with prev → curr format
function CategoryBreakdownDetail({ categories }: { categories: CategoryBreakdown[] }) {
  if (!categories || categories.length === 0) return null;
  
  // Sort by absolute delta
  const sorted = [...categories].sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
  const maxDelta = Math.max(...sorted.map(c => Math.abs(c.delta)));
  
  return (
    <div className="space-y-2.5">
      {sorted.slice(0, 5).map((cat, i) => {
        const isPositive = cat.delta > 0;
        const barWidth = maxDelta > 0 ? (Math.abs(cat.delta) / maxDelta) * 100 : 0;
        
        return (
          <div key={i} className="group">
            <div className="flex items-center justify-between mb-1">
              <span className="cs-body" style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>{cat.name}</span>
              <div className="flex items-center gap-2">
                <span className="cs-figure" style={{ color: 'var(--color-text-muted)' }}>{fmtCompact(cat.prev)}</span>
                <ArrowRight className="w-3 h-3" style={{ color: 'var(--color-text-dim)' }} />
                <span className="cs-figure" style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>{fmtCompact(cat.curr)}</span>
                <span className="cs-figure" style={{ fontWeight: 700, color: 'var(--color-needs-human-text)' }}>
                  ({isPositive ? '+' : ''}{fmtCompact(cat.delta)})
                </span>
              </div>
            </div>
            <div className="h-2 rounded-[2px] overflow-hidden" style={{ background: 'var(--color-bg-elevated)' }}>
              <div
                className="h-full rounded-[2px] transition-all"
                style={{ width: `${barWidth}%`, background: 'var(--color-brand)' }}
              />
            </div>
          </div>
        );
      })}
      {sorted.length > 5 && (
        <p className="cs-meta text-center pt-1" style={{ color: 'var(--color-text-dim)' }}>
          +{sorted.length - 5} more categories
        </p>
      )}
    </div>
  );
}

// Top Transactions List
function TopTransactions({ 
  transactions, 
  onViewTransaction 
}: { 
  transactions?: FlaggedTransaction[];
  onViewTransaction?: (id: string) => void;
}) {
  if (!transactions || transactions.length === 0) {
    return (
      <div className="cs-meta text-center py-4" style={{ color: 'var(--color-text-dim)' }}>
        <List className="w-5 h-5 mx-auto mb-1 opacity-50" />
        No transaction details available
      </div>
    );
  }

  // Sort by absolute amount to get top drivers
  const sorted = [...transactions].sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
  const top = sorted.slice(0, 4);

  return (
    <div className="space-y-1.5">
      {top.map((txn, i) => (
        <div
          key={i}
          className="flex items-center gap-2 p-2.5 rounded-lg"
          style={txn.flag
            ? { background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }
            : { background: 'var(--color-bg-elevated)' }}
        >
          {txn.flag && <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--color-brand)' }} />}
          <span className="cs-meta w-20 flex-shrink-0">{txn.date}</span>
          <span className="cs-body flex-1 truncate">{txn.description}</span>
          {txn.category && (
            <span className="cs-meta px-1.5 py-0.5 rounded" style={{ background: 'var(--color-bg-hover)', color: 'var(--color-text-muted)' }}>
              {txn.category}
            </span>
          )}
          <span className="cs-figure flex-shrink-0" style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>
            {fmtCompact(txn.amount)}
          </span>
          {onViewTransaction && txn.id && (
            <button
              onClick={(e) => { e.stopPropagation(); onViewTransaction(txn.id!); }}
              className="p-1 rounded opacity-50 hover:opacity-100 transition-opacity"
              style={{ color: 'var(--color-text-muted)' }}
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ))}
      {transactions.length > 4 && (
        <p className="cs-meta text-center pt-1" style={{ color: 'var(--color-text-dim)' }}>
          +{transactions.length - 4} more transactions
        </p>
      )}
    </div>
  );
}

// Individual Anomaly Card
function AnomalyCard({
  anomaly,
  isExpanded,
  onToggle,
  onResolve,
  onViewTransaction,
  isResolved,
  lossEventStart,
  onForensicInsightFetched,
  onExplainAnomaly
}: {
  anomaly: EnhancedAnomaly;
  isExpanded: boolean;
  onToggle: () => void;
  onResolve: (reason: string, note: string) => void;
  onViewTransaction?: (txnId: string) => void;
  isResolved: boolean;
  lossEventStart?: string;
  onForensicInsightFetched?: (anomalyId: string, insight: any) => void;
  onExplainAnomaly?: (anomalyId: string) => void;
}) {
  const [selectedReason, setSelectedReason] = useState('');
  const [note, setNote] = useState('');
  const [acceptedAI, setAcceptedAI] = useState(false);
  const [forensicInsight, setForensicInsight] = useState<any>(anomaly.forensic_analysis || null);
  const [loadingForensic, setLoadingForensic] = useState(false);

  // Hue law (K3 review 2026-08-23): the anomaly card is STRUCTURE → always
  // neutral (no severity wash). Severity is communicated by the CHIP only, and
  // severity is CRIMSON's channel — orange may not carry it. Low severity reads
  // hueless. The card surface is identical across severities.
  // Severity → color from the ONE shared source (utils/anomalySeverity). This
  // component was the canonical reference (K3 ruling above); the helper encodes
  // exactly its prior behavior — neutral card, crimson chip for critical/high,
  // muted otherwise — so every other anomaly surface now matches it.
  const sev = anomalySeverityStyle(anomaly.severity);
  const config = {
    bg: { background: sev.card.background },
    border: { borderColor: sev.card.borderColor },
    badge: sev.chip,
  };
  const isSpike = anomaly.deviation > 0;
  
  // Check for cached forensic insight
  const hasCachedForensic = anomaly.forensic_insight || anomaly.forensic_analysis;
  
  // Generate diagnostic AI text
  const diagnosticAI = generateDiagnosticSuggestion(anomaly, lossEventStart);
  const aiText = forensicInsight?.forensic_insight || anomaly.forensic_insight || diagnosticAI;
  const aiConfidence = anomaly.aiConfidence || anomaly.ai_analysis?.confidence;

  // Auto-fetch forensic insight when card is expanded (if not cached)
  React.useEffect(() => {
    if (isExpanded && !hasCachedForensic && !forensicInsight && !loadingForensic) {
      handleGetForensicInsight();
    }
  }, [isExpanded]);

  // Get forensic accountant insight
  const handleGetForensicInsight = async () => {
    setLoadingForensic(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/anomalies/forensic-analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          anomaly: anomaly,
          periods: [],
          business_context: {}
        })
      });
      const data = await response.json();
      if (data.success) {
        setForensicInsight(data);
        // Notify parent to cache the insight
        if (onForensicInsightFetched) {
          onForensicInsightFetched(anomaly.id, data);
        }
      }
    } catch (error) {
      console.error('Failed to get forensic insight:', error);
    } finally {
      setLoadingForensic(false);
    }
  };

  const handleAcceptAI = () => {
    setAcceptedAI(true);
    // Auto-select reason based on context
    if (anomaly.isLossPeriod || (lossEventStart && anomaly.period >= lossEventStart && !isSpike)) {
      setSelectedReason('Loss event impact');
    } else if (anomaly.isPreLoss && isSpike) {
      setSelectedReason('Normal fluctuation');
    } else if (anomaly.isPostLoss && isSpike) {
      setSelectedReason('Recovery period');
    } else if (isSpike) {
      setSelectedReason('One-time event');
    } else {
      setSelectedReason('Seasonal variation');
    }
    setNote(aiText);
  };

  const handleResolve = () => {
    if (selectedReason) {
      onResolve(selectedReason, note);
    }
  };

  if (isResolved) {
    return (
      <div className="p-3 rounded-[2px]" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" style={{ color: 'var(--color-verified)' }} />
          <span className="cs-body">{anomaly.title}</span>
          <span className="cs-chip px-2 py-0.5 rounded-[2px] ml-auto" style={{ background: 'transparent', color: 'var(--color-verified)', boxShadow: 'inset 0 0 0 1px var(--color-border)' }}>
            {anomaly.resolution_reason || 'Resolved'}
          </span>
        </div>
        {anomaly.resolution_note && (
          <p className="cs-meta mt-1 ml-6 italic">
            "{anomaly.resolution_note.slice(0, 100)}..."
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-[2px] border overflow-hidden transition-all duration-200" style={config.border}>
      {/* Card Header */}
      <div
        className="p-4 cursor-pointer hover:brightness-95 transition-all"
        style={config.bg}
        onClick={onToggle}
      >
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="w-10 h-10 rounded-[2px] flex items-center justify-center" style={{ background: 'var(--color-bg-elevated)' }}>
            {isSpike ? (
              <TrendingUp className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
            ) : (
              <TrendingDown className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="cs-chip px-2 py-0.5 rounded-[2px]" style={config.badge}>
                {anomaly.severity.toUpperCase()}
              </span>
              {anomaly.isLossPeriod && (
                <span className="cs-chip px-2 py-0.5 rounded" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                  LOSS PERIOD
                </span>
              )}
              <span className="cs-body" style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>{humanizeLabel(anomaly.type)}</span>
              {anomaly.deviation ? (
                <span className="cs-figure font-mono" style={{ fontWeight: 700, color: 'var(--color-text-secondary)' }}>
                  {pct(anomaly.deviation)}
                </span>
              ) : null}
            </div>

            <p className="cs-body">{anomaly.title || anomaly.description}</p>

            <div className="flex items-center gap-4 mt-2 cs-meta">
              <span>Period: <strong style={{ color: 'var(--color-text-primary)' }}>{anomaly.period}</strong></span>
              {(anomaly.hasDelta ?? (anomaly.from !== 0 || anomaly.to !== 0)) ? (
                <span className="cs-figure flex items-center gap-1">
                  {fmtCompact(anomaly.from)}
                  <ArrowRight className="w-3 h-3" />
                  {fmtCompact(anomaly.to)}
                </span>
              ) : anomaly.relatedAmount != null ? (
                <span className="cs-figure">{fmtCompact(anomaly.relatedAmount)}</span>
              ) : null}
            </div>
          </div>
          
          {/* Expand Button + Why Flagged */}
          <div className="flex items-center gap-1">
            {/* Why flagged? button — opens SHAP explainability drawer */}
            {onExplainAnomaly && (
              <button
                onClick={(e) => { e.stopPropagation(); onExplainAnomaly(anomaly.id); }}
                className="cs-chip flex items-center gap-1.5 px-3 py-1.5 rounded-[2px] transition-colors"
                style={{ background: 'transparent', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)' }}
                title="Explain why this was flagged"
              >
                <HelpCircle className="w-3.5 h-3.5" />
                Why flagged?
              </button>
            )}
            <button className="p-1 rounded-lg transition-colors" style={{ color: 'var(--color-text-muted)' }}>
              {isExpanded ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Expanded Detail */}
      {isExpanded && (
        <div className="p-4 border-t" style={{ background: 'var(--color-bg-surface)', borderColor: 'var(--color-border-sub)' }}>
          {/* Forensic Accountant Insight */}
          {forensicInsight && (
            <div className="rounded-lg p-4 mb-4" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-needs-human-tint)' }}>
                  <FileText className="w-4 h-4" style={{ color: 'var(--color-brand)' }} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="cs-eyebrow" style={{ color: 'var(--color-needs-human-text)' }}>
                      FORENSIC ACCOUNTANT ANALYSIS
                    </span>
                    {forensicInsight.claim_impact && (
                      <span
                        className="cs-chip px-2 py-0.5 rounded-[2px]"
                        style={
                          forensicInsight.claim_impact === 'HELPS_CLAIM'
                            ? { background: 'transparent', color: 'var(--color-verified)' }
                            : forensicInsight.claim_impact === 'HURTS_CLAIM'
                              ? { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                              : { background: 'var(--color-bg-hover)', color: 'var(--color-text-muted)' }
                        }
                      >
                        {humanizeLabel(forensicInsight.claim_impact)}
                      </span>
                    )}
                  </div>
                  <p className="cs-body mb-2">
                    {forensicInsight.forensic_insight}
                  </p>
                  {forensicInsight.recommended_action && (
                    <p className="cs-meta" style={{ fontWeight: 500, color: 'var(--color-needs-human-text)' }}>
                      → {forensicInsight.recommended_action}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* AI Diagnostic Suggestion */}
          {aiText && !forensicInsight && (
            <div
              className="rounded-lg p-4 mb-4"
              style={acceptedAI
                ? { background: 'transparent', border: '1px solid var(--color-verified)' }
                : { background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={acceptedAI
                    ? { background: 'transparent' }
                    : { background: 'var(--color-needs-human-tint)' }}
                >
                  {acceptedAI ? (
                    <Check className="w-4 h-4" style={{ color: 'var(--color-verified)' }} />
                  ) : (
                    <Sparkles className="w-4 h-4" style={{ color: 'var(--color-brand)' }} />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="cs-eyebrow" style={{ color: acceptedAI ? 'var(--color-verified)' : 'var(--color-needs-human-text)' }}>
                      AI DIAGNOSTIC
                    </span>
                    {aiConfidence && (
                      <span
                        className="cs-chip px-2 py-0.5 rounded-[2px]"
                        style={acceptedAI
                          ? { background: 'transparent', color: 'var(--color-verified)' }
                          : { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
                      >
                        {aiConfidence}% confidence
                      </span>
                    )}
                  </div>
                  <p className="cs-body" style={{ color: acceptedAI ? 'var(--color-text-primary)' : 'var(--color-text-secondary)' }}>
                    {aiText}
                  </p>
                  <div className="flex gap-2 mt-3">
                    {!acceptedAI && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleAcceptAI(); }}
                        className="cs-body px-4 py-1.5 rounded-lg transition-colors flex items-center gap-2"
                        style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 500 }}
                      >
                        <Check className="w-4 h-4" /> Accept & Use in Report
                      </button>
                    )}
                    {!forensicInsight && !loadingForensic && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleGetForensicInsight(); }}
                        className="cs-body px-4 py-1.5 rounded-lg transition-colors flex items-center gap-2"
                        style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)', fontWeight: 500, border: '1px solid var(--color-border)' }}
                      >
                        <FileText className="w-4 h-4" /> Deep Analysis
                      </button>
                    )}
                    {loadingForensic && (
                      <span className="cs-body px-4 py-1.5 flex items-center gap-2" style={{ color: 'var(--color-text-muted)' }}>
                        <Loader2 className="w-4 h-4 animate-spin" /> Analyzing...
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Two Column Layout: Categories + Transactions */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            {/* Category Breakdown */}
            <div>
              <h4 className="cs-label mb-3 flex items-center gap-2">
                <TrendingDown className="w-3.5 h-3.5" />
                Category Breakdown
              </h4>
              <CategoryBreakdownDetail categories={anomaly.categories || []} />
            </div>

            {/* Top Transactions */}
            <div>
              <h4 className="cs-label mb-3 flex items-center gap-2">
                <List className="w-3.5 h-3.5" />
                Top Transactions
              </h4>
              <TopTransactions
                transactions={anomaly.transactions}
                onViewTransaction={onViewTransaction}
              />
            </div>
          </div>

          {/* Source Document */}
          {anomaly.sourceDoc && (
            <div className="cs-meta flex items-center gap-2 mb-4">
              <FileText className="w-3 h-3" />
              <span>Source: {anomaly.sourceDoc}</span>
            </div>
          )}

          {/* Report Flow Notice */}
          <div className="rounded-lg p-3 mb-4" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
            <p className="cs-meta flex items-start gap-2">
              <FileText className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-text-dim)' }} />
              <span>
                <strong style={{ color: 'var(--color-text-primary)' }}>Expert Report Integration:</strong> Your resolution reason and note will automatically
                appear in the final expert report narrative. Be specific — this becomes testimony support.
              </span>
            </p>
          </div>

          {/* Resolution Section */}
          <div className="pt-4 border-t" style={{ borderColor: 'var(--color-border-sub)' }}>
            <h4 className="cs-label mb-3">
              Resolution
            </h4>

            {/* Reason Pills */}
            <div className="flex flex-wrap gap-2 mb-3">
              {RESOLUTION_REASONS.map(reason => (
                <button
                  key={reason}
                  onClick={(e) => { e.stopPropagation(); setSelectedReason(reason); }}
                  className="cs-chip px-3 py-1.5 rounded-[2px] transition-all"
                  style={selectedReason === reason
                    ? { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', border: '1px solid var(--color-needs-human-tint-bd)' }
                    : { background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)', border: '1px solid transparent' }}
                >
                  {reason}
                </button>
              ))}
            </div>

            {/* Note Input */}
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add analysis note (this appears in the expert report)..."
              className="cs-body w-full p-3 rounded-lg resize-none mb-3"
              style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
              rows={3}
              onClick={(e) => e.stopPropagation()}
            />

            {/* Resolve Button */}
            <button
              onClick={(e) => { e.stopPropagation(); handleResolve(); }}
              disabled={!selectedReason}
              className="cs-body w-full py-3 rounded-lg transition-all flex items-center justify-center gap-2"
              style={selectedReason
                ? { background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 600 }
                : { background: 'var(--color-bg-elevated)', color: 'var(--color-text-dim)', fontWeight: 600, cursor: 'not-allowed' }}
            >
              <CheckCircle2 className="w-4 h-4" />
              {selectedReason ? `Resolve as "${selectedReason}"` : 'Select a reason to resolve'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Enhanced Batch Resolve Modal with Filters
function BatchResolveModal({
  visible,
  onClose,
  anomalies,
  resolvedIds,
  onBatchResolve,
  lossEventStart,
  lossEventEnd
}: {
  visible: boolean;
  onClose: () => void;
  anomalies: EnhancedAnomaly[];
  resolvedIds: string[];
  onBatchResolve: (ids: string[], reason: string, note: string) => void;
  lossEventStart?: string;
  lossEventEnd?: string;
}) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');

  const unresolvedAnomalies = anomalies.filter(a => !resolvedIds.includes(a.id));
  
  // Apply filter
  const filteredAnomalies = useMemo(() => {
    return unresolvedAnomalies.filter(a => {
      const isSpike = a.deviation > 0;
      const isDrop = a.deviation < 0;
      const isHigh = a.severity === 'high' || a.severity === 'critical';
      const isPreLoss = a.isPreLoss || (lossEventStart && a.period < lossEventStart);
      const isLossPeriod = a.isLossPeriod || (lossEventStart && lossEventEnd && 
        a.period >= lossEventStart && a.period <= lossEventEnd);
      
      switch (activeFilter) {
        case 'spikes': return isSpike;
        case 'drops': return isDrop;
        case 'high': return isHigh;
        case 'pre-loss': return isPreLoss;
        case 'loss-period': return isLossPeriod;
        default: return true;
      }
    });
  }, [unresolvedAnomalies, activeFilter, lossEventStart, lossEventEnd]);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectAllFiltered = () => {
    setSelectedIds(filteredAnomalies.map(a => a.id));
  };

  const clearSelection = () => {
    setSelectedIds([]);
  };

  const handleResolve = () => {
    if (selectedIds.length > 0 && reason) {
      onBatchResolve(selectedIds, reason, note);
      setSelectedIds([]);
      setReason('');
      setNote('');
      onClose();
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="rounded-[2px] shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between flex-shrink-0" style={{ borderColor: 'var(--color-border)' }}>
          <div>
            <h3 className="cs-h3 flex items-center gap-2">
              <Zap className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
              Batch Resolve Anomalies
            </h3>
            <p className="cs-meta mt-1">
              Filter and resolve multiple anomalies with a shared explanation
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg" style={{ color: 'var(--color-text-muted)' }}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filter Bar */}
        <div className="p-3 border-b flex-shrink-0" style={{ borderColor: 'var(--color-border-sub)', background: 'var(--color-bg-base)' }}>
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <Filter className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-text-dim)' }} />
            {BATCH_FILTERS.map(filter => {
              const count = unresolvedAnomalies.filter(a => {
                const isSpike = a.deviation > 0;
                const isDrop = a.deviation < 0;
                const isHigh = a.severity === 'high' || a.severity === 'critical';
                const isPreLoss = a.isPreLoss || (lossEventStart && a.period < lossEventStart);
                const isLossPeriod = a.isLossPeriod || (lossEventStart && lossEventEnd && 
                  a.period >= lossEventStart && a.period <= lossEventEnd);
                
                switch (filter.id) {
                  case 'spikes': return isSpike;
                  case 'drops': return isDrop;
                  case 'high': return isHigh;
                  case 'pre-loss': return isPreLoss;
                  case 'loss-period': return isLossPeriod;
                  default: return true;
                }
              }).length;
              
              return (
                <button
                  key={filter.id}
                  onClick={() => { setActiveFilter(filter.id); setSelectedIds([]); }}
                  className="cs-chip px-3 py-1.5 rounded-[2px] whitespace-nowrap transition-all flex items-center gap-1.5"
                  style={activeFilter === filter.id
                    ? { ...ACTIVE_FILTER_STYLE, borderWidth: 1, borderStyle: 'solid' }
                    : { background: 'var(--color-bg-surface)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)' }}
                >
                  {filter.label}
                  <span className="px-1.5 py-0.5 rounded text-[10px]" style={{ background: 'var(--color-bg-elevated)' }}>{count}</span>
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Selection Actions */}
        <div className="px-4 py-2 border-b flex items-center justify-between flex-shrink-0" style={{ borderColor: 'var(--color-border-sub)' }}>
          <span className="cs-body">
            <strong style={{ color: 'var(--color-text-primary)' }}>{selectedIds.length}</strong> of {filteredAnomalies.length} selected
          </span>
          <div className="flex gap-3">
            <button
              onClick={clearSelection}
              className="cs-body"
              style={{ color: 'var(--color-text-muted)' }}
              disabled={selectedIds.length === 0}
            >
              Clear
            </button>
            <button
              onClick={selectAllFiltered}
              className="cs-body"
              style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}
            >
              Select All ({filteredAnomalies.length})
            </button>
          </div>
        </div>
        
        {/* Anomaly List */}
        <div className="flex-1 overflow-y-auto p-4">
          {filteredAnomalies.length === 0 ? (
            <div className="cs-body text-center py-8" style={{ color: 'var(--color-text-dim)' }}>
              <Filter className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No anomalies match this filter</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredAnomalies.map(a => {
                const isSpike = a.deviation > 0;
                return (
                  <div
                    key={a.id}
                    onClick={() => toggleSelect(a.id)}
                    className="p-3 rounded-lg border cursor-pointer transition-all"
                    style={selectedIds.includes(a.id)
                      ? { borderColor: 'var(--color-needs-human-tint-bd)', background: 'var(--color-needs-human-tint)' }
                      : { borderColor: 'var(--color-border)', background: 'var(--color-bg-surface)' }}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0"
                        style={selectedIds.includes(a.id)
                          ? { border: '2px solid var(--color-brand)', background: 'var(--color-brand)' }
                          : { border: '2px solid var(--color-border)' }}
                      >
                        {selectedIds.includes(a.id) && <Check className="w-3 h-3" style={{ color: 'var(--color-on-brand)' }} />}
                      </div>
                      <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0" style={{ background: 'var(--color-needs-human-tint)' }}>
                        {isSpike ? (
                          <TrendingUp className="w-3.5 h-3.5" style={{ color: 'var(--color-brand)' }} />
                        ) : (
                          <TrendingDown className="w-3.5 h-3.5" style={{ color: 'var(--color-brand)' }} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="cs-body truncate">{humanizeLabel(a.type)}</span>
                          {a.deviation ? (
                            <span className="cs-body" style={{ fontWeight: 700, color: 'var(--color-needs-human-text)' }}>
                              {pct(a.deviation)}
                            </span>
                          ) : null}
                        </div>
                        <div className="cs-figure" style={{ color: 'var(--color-text-muted)' }}>
                          {(a.hasDelta ?? (a.from !== 0 || a.to !== 0))
                            ? `${fmtCompact(a.from)} → ${fmtCompact(a.to)}`
                            : (a.relatedAmount != null ? fmtCompact(a.relatedAmount) : '')}
                        </div>
                      </div>
                      <span className="cs-meta flex-shrink-0">{a.period}</span>
                      <span
                        className="cs-chip px-2 py-0.5 rounded flex-shrink-0"
                        style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
                      >
                        {a.severity.toUpperCase()}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Resolution Form */}
        <div className="p-4 border-t flex-shrink-0" style={{ borderColor: 'var(--color-border)', background: 'var(--color-bg-base)' }}>
          {/* Reason Pills */}
          <div className="flex flex-wrap gap-2 mb-3">
            {RESOLUTION_REASONS.map(r => (
              <button
                key={r}
                onClick={() => setReason(r)}
                className="cs-chip px-3 py-1.5 rounded-[2px] transition-all"
                style={reason === r
                  ? { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', border: '1px solid var(--color-needs-human-tint-bd)' }
                  : { background: 'var(--color-bg-surface)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)' }}
              >
                {r}
              </button>
            ))}
          </div>

          {/* Note */}
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Shared explanation for all selected anomalies (appears in report)..."
            className="cs-body w-full p-3 rounded-lg resize-none mb-3"
            style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
            rows={2}
          />

          {/* Action Button */}
          <button
            onClick={handleResolve}
            disabled={!selectedIds.length || !reason}
            className="cs-body w-full py-3 rounded-lg transition-all flex items-center justify-center gap-2"
            style={selectedIds.length && reason
              ? { background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 600 }
              : { background: 'var(--color-bg-elevated)', color: 'var(--color-text-dim)', fontWeight: 600, cursor: 'not-allowed' }}
          >
            <CheckCircle2 className="w-4 h-4" />
            Resolve {selectedIds.length} Anomalies as "{reason || '...'}"
          </button>
        </div>
      </div>
    </div>
  );
}

// Main Component
export default function AnomalyResolver({
  anomalies,
  periodsData,
  totalTransactions = 0,
  totalMonths = 0,
  lossEventStart,
  lossEventEnd,
  onResolve,
  onBatchResolve,
  onUnresolve,
  onViewTransaction,
  onForensicInsightFetched,
  onExplainAnomaly,
  loading = false
}: AnomalyResolverProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [resolvedIds, setResolvedIds] = useState<string[]>(() => 
    anomalies.filter(a => a.is_resolved).map(a => a.id)
  );
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [visibleCount, setVisibleCount] = useState(5);

  const unresolvedCount = anomalies.length - resolvedIds.length;
  const highPriorityCount = anomalies.filter(a => 
    !resolvedIds.includes(a.id) && (a.severity === 'high' || a.severity === 'critical')
  ).length;

  const handleResolve = (id: string, reason: string, note: string) => {
    setResolvedIds(prev => [...prev, id]);
    setExpandedId(null);
    onResolve(id, reason, note);
  };

  const handleBatchResolve = (ids: string[], reason: string, note: string) => {
    setResolvedIds(prev => [...new Set([...prev, ...ids])]);
    onBatchResolve(ids, reason, note);
  };

  // Generate periods data from anomalies if not provided
  // Build timeline data - prefer full periods data if available
  // If only anomalies, show a simplified bar chart instead of sparkline
  const timelineData = useMemo(() => {
    // If full periods data is provided, use it
    if (periodsData && periodsData.length > 0) {
      // Mark which periods have anomalies
      const anomalyPeriods = new Set(anomalies.map(a => a.period));
      return periodsData.map((p): PeriodData => ({
        ...p,
        anomaly: anomalyPeriods.has(p.month)
          ? ((anomalies.find(a => a.period === p.month)?.deviation || 0) > 0 ? 'spike' as const : 'drop' as const)
          : undefined
      }));
    }
    
    // Fallback: build from anomalies - but aggregate by month to avoid duplicates
    // This won't show the full revenue trend, just anomaly periods
    const monthMap = new Map<string, { revenue: number; anomaly?: 'spike' | 'drop' }>();
    
    anomalies.forEach(a => {
      const anomalyType = a.deviation > 0 ? 'spike' : 'drop';
      // Only keep the most significant anomaly for each period
      const existing = monthMap.get(a.period);
      if (!existing || Math.abs(a.deviation) > Math.abs(existing.revenue)) {
        monthMap.set(a.period, { revenue: a.to, anomaly: anomalyType });
      }
    });
    
    return Array.from(monthMap.entries())
      .map(([month, data]) => ({ month, ...data }))
      .sort((a, b) => a.month.localeCompare(b.month));
  }, [anomalies, periodsData]);

  // Check if we have proper timeline data or just anomaly points
  const hasFullTimeline = periodsData && periodsData.length > 0;

  // Separate resolved and unresolved
  const unresolvedAnomalies = anomalies.filter(a => !resolvedIds.includes(a.id));
  const resolvedAnomalies = anomalies.filter(a => resolvedIds.includes(a.id));

  if (loading) {
    return (
      <div className="rounded-[2px] p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
        <div className="flex items-center gap-4">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-brand)' }} />
          <div>
            <p className="cs-body" style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>Analyzing Financial Data...</p>
            <p className="cs-meta" style={{ color: 'var(--color-text-muted)' }}>Detecting anomalies and generating AI insights</p>
          </div>
        </div>
      </div>
    );
  }

  if (anomalies.length === 0) {
    return (
      <div className="rounded-[2px] p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
        <div className="flex items-center gap-3">
          <CheckCircle2 className="w-6 h-6" style={{ color: 'var(--color-verified)' }} />
          <div>
            <p className="cs-body" style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>No Anomalies Detected</p>
            <p className="cs-meta" style={{ color: 'var(--color-verified)' }}>AI verified data consistency across all periods</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="cs-eyebrow flex items-center gap-2 mb-1">
          <span>FINANCIAL DATA</span>
          <span>•</span>
          <span>ANOMALY RESOLUTION</span>
        </div>
        <h2 className="cs-display">
          {unresolvedCount} Anomal{unresolvedCount === 1 ? 'y' : 'ies'} to Review
        </h2>
        <p className="cs-body mt-1">
          {totalTransactions.toLocaleString()} transactions across {totalMonths} months
          {highPriorityCount > 0 && (
            <span className="ml-2" style={{ color: 'var(--color-needs-human-text)' }}>• {highPriorityCount} high priority</span>
          )}
          {resolvedIds.length > 0 && (
            <span className="ml-2" style={{ color: 'var(--color-verified)' }}>• {resolvedIds.length} resolved</span>
          )}
        </p>
      </div>

      {/* Anomaly Distribution Summary - shows when we have many anomalies */}
      {anomalies.length > 10 && (
        <div className="rounded-[2px] p-5" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="cs-label">
              Anomaly Distribution
            </h3>
            <div className="flex items-center gap-4 cs-meta">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full" style={{ background: 'var(--chart-1)' }} />
                Spikes ({anomalies.filter(a => a.deviation > 0).length})
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full" style={{ background: 'var(--color-blocker)' }} />
                Drops ({anomalies.filter(a => a.deviation < 0).length})
              </span>
            </div>
          </div>
          
          {/* Mini bar chart showing anomaly severity distribution */}
          {(() => {
            // Hoisted severity counts. Pre-PR-#232 these were re-computed
            // inline four times inside style={{height:...}} expressions, with
            // the first one using a buggy `.map(()=>1).reduce(...)` index-shift
            // that ALSO caused TS2488/TS2345 because it spread a Record into
            // Math.max. The bottom block at L1377 was the correct shape, so
            // extracting it once and reusing for both bars matches that intent.
            const severityCount = anomalies.reduce((acc, a) => {
              acc[a.severity] = (acc[a.severity] || 0) + 1;
              return acc;
            }, {} as Record<string, number>);
            const maxSeverity = Math.max(...Object.values(severityCount), 1);
            return (
              <div className="grid grid-cols-4 gap-2 mb-3">
                {['critical', 'high', 'medium', 'low'].map(severity => {
                  const count = anomalies.filter(a => a.severity === severity && !resolvedIds.includes(a.id)).length;
                  const resolvedCount = anomalies.filter(a => a.severity === severity && resolvedIds.includes(a.id)).length;
                  // Step the on-brand chart ramp by severity so the tiers are
                  // distinguishable at a glance (not just by bar height):
                  // critical = deepest burnt orange → low = neutral.
                  const barColor = ({
                    critical: 'var(--color-blocker)',
                    high:     'var(--chart-1)',
                    medium:   'var(--chart-2)',
                    low:      'var(--chart-4)',
                  } as Record<string, string>)[severity] || 'var(--color-brand)';
                  return (
                    <div key={severity} className="text-center">
                      <div className="h-12 rounded relative overflow-hidden flex flex-col justify-end" style={{ background: 'var(--color-bg-elevated)' }}>
                        {resolvedCount > 0 && (
                          <div
                            className="opacity-30"
                            style={{ height: `${(resolvedCount / maxSeverity) * 100}%`, background: barColor }}
                          />
                        )}
                        <div
                          style={{ height: `${(count / maxSeverity) * 100}%`, background: barColor }}
                        />
                      </div>
                      <div className="cs-meta mt-1 capitalize" style={{ fontWeight: 500, color: 'var(--color-text-secondary)' }}>{severity}</div>
                      <div className="text-[10px]" style={{ color: 'var(--color-text-dim)' }}>{count} left</div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
          
          {/* Period range */}
          {timelineData.length >= 2 && (
            <div className="cs-meta flex justify-between border-t pt-3" style={{ color: 'var(--color-text-dim)', borderColor: 'var(--color-border-sub)' }}>
              <span>First: {timelineData[0].month}</span>
              <span>Latest: {timelineData[timelineData.length - 1].month}</span>
            </div>
          )}
        </div>
      )}

      {/* Revenue Timeline - only show if we have proper period data (not just anomalies) */}
      {hasFullTimeline && timelineData.length > 0 && (
        <div className="rounded-[2px] p-5" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="cs-label">
              Revenue Timeline
            </h3>
            <div className="flex items-center gap-4 cs-meta">
              {lossEventStart && (
                <span className="flex items-center gap-1">
                  <span className="w-3 h-2 rounded-[2px]" style={{ background: 'var(--color-text-dim)' }} />
                  Loss Period
                </span>
              )}
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full" style={{ background: 'var(--chart-1)' }} />
                Spike
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full" style={{ background: 'var(--color-blocker)' }} />
                Drop
              </span>
            </div>
          </div>
          <div className="h-48 relative">
            <Sparkline
              data={timelineData}
              activeMonth={expandedId ? anomalies.find(a => a.id === expandedId)?.period : undefined}
              lossEventStart={lossEventStart}
              lossEventEnd={lossEventEnd}
              onClickMonth={(month) => {
                const anomaly = anomalies.find(a => a.period === month);
                if (anomaly) setExpandedId(anomaly.id);
              }}
            />
          </div>
        </div>
      )}

      {/* Action Bar */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => setShowBatchModal(true)}
          className="cs-body px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
          style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)', fontWeight: 500 }}
        >
          <Zap className="w-4 h-4" />
          Batch Resolve ({unresolvedCount})
        </button>
        <div className="flex-1" />
        <span className="cs-meta">
          {resolvedIds.length}/{anomalies.length} resolved
        </span>
        <div className="w-32 h-2 rounded-[2px] overflow-hidden" style={{ background: 'var(--color-bg-elevated)' }}>
          <div
            className="h-full rounded-[2px] transition-all duration-500"
            style={{ width: `${(resolvedIds.length / anomalies.length) * 100}%`, background: 'var(--color-verified)' }}
          />
        </div>
      </div>

      {/* Unresolved Anomaly Cards */}
      <div className="space-y-3">
        {unresolvedAnomalies.slice(0, visibleCount).map(anomaly => (
          <AnomalyCard
            key={anomaly.id}
            anomaly={anomaly}
            isExpanded={expandedId === anomaly.id}
            onToggle={() => setExpandedId(expandedId === anomaly.id ? null : anomaly.id)}
            onResolve={(reason, note) => handleResolve(anomaly.id, reason, note)}
            onViewTransaction={onViewTransaction}
            isResolved={false}
            lossEventStart={lossEventStart}
            onForensicInsightFetched={onForensicInsightFetched}
            onExplainAnomaly={onExplainAnomaly}
          />
        ))}
        
        {/* Show More Button */}
        {unresolvedAnomalies.length > visibleCount && (
          <button
            onClick={() => setVisibleCount(prev => prev + 10)}
            className="cs-body w-full py-3 rounded-lg transition-colors"
            style={{ border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)', fontWeight: 500 }}
          >
            Show {Math.min(10, unresolvedAnomalies.length - visibleCount)} More
            ({unresolvedAnomalies.length - visibleCount} remaining)
          </button>
        )}
      </div>

      {/* Resolved Section */}
      {resolvedAnomalies.length > 0 && (
        <div className="pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
          <h3 className="cs-label mb-3">
            Resolved ({resolvedAnomalies.length})
          </h3>
          <div className="space-y-2">
            {resolvedAnomalies.slice(0, 3).map(anomaly => (
              <AnomalyCard
                key={anomaly.id}
                anomaly={anomaly}
                isExpanded={false}
                onToggle={() => {}}
                onResolve={() => {}}
                isResolved={true}
              />
            ))}
            {resolvedAnomalies.length > 3 && (
              <p className="cs-meta text-center py-2" style={{ color: 'var(--color-text-dim)' }}>
                +{resolvedAnomalies.length - 3} more resolved
              </p>
            )}
          </div>
        </div>
      )}

      {/* Batch Resolve Modal */}
      <BatchResolveModal
        visible={showBatchModal}
        onClose={() => setShowBatchModal(false)}
        anomalies={anomalies}
        resolvedIds={resolvedIds}
        onBatchResolve={handleBatchResolve}
        lossEventStart={lossEventStart}
        lossEventEnd={lossEventEnd}
      />
    </div>
  );
}
